import { z } from "zod";
import { summary as planogramSummary } from "../fixture-planograms/fixture-planograms.schemas.js";
import { categoryInfo } from "../categories/categories.schemas.js";
import { auditActorFields } from "../common/audit.js";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
import { booleanQueryParam } from "../common/query-params.js";
export const fixtureInfo = z.object({
    planogram: planogramSummary.optional(),
    id: z.string().uuid(),
    storeId: z.string().uuid(),
    storeName: z.string(),
    storeCode: z.string(),
    zoneId: z.string().uuid().nullable(),
    zoneName: z.string().nullable(),
    storeMapId: z.string().uuid().nullable(),
    categoryId: z.string().uuid().nullable(),
    categoryName: z.string().nullable(),
    code: z.string(),
    name: z.string().nullable(),
    fixtureType: z.string().nullable(),
    widthMm: z.number().int().nullable(),
    heightMm: z.number().int().nullable(),
    depthMm: z.number().int().nullable(),
    mapCoordinates: z.record(z.unknown()),
    annotatePose: z.record(z.unknown()),
    sortOrder: z.number().int().nullable(),
    isActive: z.boolean(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    ...auditActorFields,
});
export const createFixtureRequest = z.object({
    storeId: z.string().uuid(),
    zoneId: z.string().uuid().nullish(),
    storeMapId: z.string().uuid().nullish(),
    categoryId: z.string().uuid().nullish(),
    code: z.string().min(1).max(100),
    name: z.string().min(1).max(255).nullish(),
    fixtureType: z.string().max(64).nullish(),
    widthMm: z.number().int().positive().nullish(),
    heightMm: z.number().int().positive().nullish(),
    depthMm: z.number().int().positive().nullish(),
    mapCoordinates: z.record(z.unknown()).default({}),
    annotatePose: z.record(z.unknown()).default({}),
    sortOrder: z.number().int().nullish(),
    isActive: z.boolean().default(true),
    metadata: z.record(z.unknown()).default({}),
});
export const updateFixtureRequest = createFixtureRequest
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field required" });
export const listFixturesQuery = paginationQuerySchema.extend({
    planogramState: z.enum(["none", "draft", "active", "active_with_draft"]).optional(),
    storeId: z.string().uuid().optional(),
    // A zone id scopes to that zone; the literal "none" scopes to fixtures with NO
    // zone assigned; omitted = all zones.
    zoneId: z.union([z.string().uuid(), z.literal("none")]).optional(),
    // A category id scopes to that category; the literal "none" scopes to fixtures
    // with NO category assigned; omitted = all categories.
    categoryId: z.union([z.string().uuid(), z.literal("none")]).optional(),
    /** Scope to one map (e.g. the active map) — fixtures are per-map. */
    storeMapId: z.string().uuid().optional(),
    isActive: booleanQueryParam.optional(),
    /** Case-insensitive substring match on fixture code or name. */
    search: z.string().max(255).optional(),
});
export const fixturesListResponse = pagedResponse(fixtureInfo);
// ── Fixture ↔ category linking ────────────────────────────────────────────────
// A fixture references a single category (fixtures.category_id). The
// `/fixtures/:fixtureId/categories` endpoint lists it (0–1 items) and links one.
export const linkFixtureCategoryRequest = z.object({
    categoryId: z.string().uuid(),
});
export const fixtureCategoriesListResponse = z.object({
    items: z.array(categoryInfo),
});
// ── Bulk fixture editing (name + category + zone) ───────────────────────────────
// Edit the name, category and/or zone of many fixtures in one call. Each item
// targets one fixture by id; a field that is PRESENT is written (`categoryId`/
// `zoneId: null` clears it, a blank `name` clears the custom name), a field that
// is OMITTED is left unchanged. At least one editable field is required per item.
// Backs the bulk fixture-editing screen; the response reports how many fixtures
// were updated.
export const bulkUpdateFixturesItem = z
    .object({
    id: z.string().uuid(),
    categoryId: z.string().uuid().nullable().optional(),
    zoneId: z.string().uuid().nullable().optional(),
    name: z.string().trim().max(255).nullable().optional(),
})
    .refine((v) => v.categoryId !== undefined || v.zoneId !== undefined || v.name !== undefined, {
    message: "Each item must change a name, a category or a zone",
});
export const bulkUpdateFixturesRequest = z.object({
    items: z.array(bulkUpdateFixturesItem).min(1).max(500),
});
export const bulkUpdateFixturesResponse = z.object({
    updated: z.number().int(),
});
//# sourceMappingURL=fixtures.schemas.js.map